import { getLogger } from "@expert/logging";
import { getFeature } from "@soluto-private/expert-workspace-feature-flagging";
import { useQuery } from "@tanstack/react-query";

const hostingEnvironments = Object.freeze(["serverless", "k8s"] as const);

export type HostingEnvironment = (typeof hostingEnvironments)[number];

let current: HostingEnvironment = "serverless";

function assertHostingEnvironment(value: string | undefined): asserts value is HostingEnvironment {
    if (value != null && (hostingEnvironments as readonly string[]).includes(value)) {
        return;
    }

    throw new Error(`Invalid hosting environment value '${value}'.`);
}

const fetchHostingEnvironment = async (context: Record<string, unknown>): Promise<string> => {
    const logger = getLogger({ module: "hostingEnvStore" });
    try {
        const hostingEnvironment = await getFeature<string>("EWP-hostingEnvironment", context);
        assertHostingEnvironment(hostingEnvironment);
        current = hostingEnvironment;
        return current;
    } catch (error) {
        logger.warn({ error }, "Error fetching hosting environment");
        // TODO: Default to serverless until the kubernetes migration is complete
        //       Once the migration is complete, default to k8s
        return "serverless";
    }
};

export function getHostingEnv(): HostingEnvironment {
    return current;
}

/** Waits for hosting environment to be resolved (e.g. before using getBackendUrls()). */
export function useHostingEnvironment(employeeId: string | undefined, channel?: string) {
    const context = { employeeId, channel };
    return useQuery({
        queryKey: ["hosting-environment", employeeId, channel],
        queryFn: () => fetchHostingEnvironment(context),
        retry: 0,
        refetchOnWindowFocus: false,
        enabled: !!employeeId,
    });
}
